import React, { useState } from 'react';
import {
  ArrowRightLeft,
  CalendarDays,
  History,
  MessageSquareMore,
  Search,
  UserCheck,
  UserMinus,
  Users,
} from 'lucide-react';
import { cn } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import ListItemCard from '../../../components/common/ListItemCard';

export interface ClassStudentsPanelStudent {
  id: string;
  name: string;
  ageGroup: string;
  ageLabel: string;
  joinedEbdAtLabel: string;
  attendanceLabel: string;
  attendanceRate: number;
  classHistoryCount: number;
  recurringAbsentee: boolean;
  lowAttendance: boolean;
  notes: string;
  consecutiveAbsences: number;
  ageMismatch?: boolean;
  ageMismatchLabel?: string;
}

export interface ClassStudentsPanelVisitor {
  id: string;
  name: string;
  whatsapp: string;
  visitCount: number;
  lastVisitLabel: string;
  convertedToStudentId?: string | null;
}

interface ClassStudentsPanelProps {
  items: ClassStudentsPanelStudent[];
  visitors: ClassStudentsPanelVisitor[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onClearFilters: () => void;
  onOpenAttendanceHistory: (studentId: string) => void;
  onEditStudent: (studentId: string) => void;
  onTransferStudent: (studentId: string) => void;
  onRemoveStudent: (studentId: string) => void;
  onViewHistory: (studentId: string) => void;
  onViewVisitorHistory: (visitorId: string) => void;
  onConvertVisitor: (visitorId: string) => void;
}

const ClassStudentsPanel: React.FC<ClassStudentsPanelProps> = ({
  items,
  visitors,
  searchValue,
  onSearchChange,
  onClearFilters,
  onOpenAttendanceHistory,
  onEditStudent,
  onTransferStudent,
  onRemoveStudent,
  onViewHistory,
  onViewVisitorHistory,
  onConvertVisitor,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  return (
    <section className="space-y-4">
      <Card className="space-y-4 border-slate-200 bg-white p-4 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.3)]">
        <div className="relative">
          <Input
            placeholder="Buscar aluno ou observação..."
            className="pl-11"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        </div>

        {searchValue && (
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClearFilters}
              className={cn(
                'rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-600',
                'ml-auto'
              )}
            >
              Limpar filtros
            </button>
          </div>
        )}
      </Card>

      {items.length > 0 ? (
        <div className="space-y-4">
          {items.map((item) => (
            <ListItemCard
              key={item.id}
              title={item.name}
              onClick={() => onOpenAttendanceHistory(item.id)}
              leadingIcon={Users}
              leadingTone="from-sky-100 to-emerald-100 text-sky-700"
              badges={[
                ...(item.lowAttendance
                  ? [
                      {
                        label: `Atenção: ${item.attendanceRate}% no trimestre`,
                        className: 'bg-amber-50 text-amber-800 ring-1 ring-amber-200',
                      },
                    ]
                  : []),
                ...(item.ageMismatch
                  ? [
                      {
                        label: item.ageMismatchLabel ?? 'Fora da faixa etária da turma',
                        className: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
                      },
                    ]
                  : []),
                {
                  label: item.attendanceLabel,
                  className: item.recurringAbsentee ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700',
                },
              ]}
              menuOpen={openMenuId === item.id}
              onToggleMenu={() => setOpenMenuId(openMenuId === item.id ? null : item.id)}
              actions={[
                {
                  label: 'Editar vínculo',
                  icon: MessageSquareMore,
                  tone: 'bg-sky-50 text-sky-600',
                  onClick: () => {
                    setOpenMenuId(null);
                    onEditStudent(item.id);
                  },
                },
                {
                  label: 'Ver histórico',
                  icon: CalendarDays,
                  tone: 'bg-emerald-50 text-emerald-600',
                  onClick: () => {
                    setOpenMenuId(null);
                    onViewHistory(item.id);
                  },
                },
                {
                  label: 'Transferir',
                  icon: ArrowRightLeft,
                  tone: 'bg-amber-50 text-amber-700',
                  onClick: () => {
                    setOpenMenuId(null);
                    onTransferStudent(item.id);
                  },
                },
                {
                  label: 'Remover',
                  icon: UserMinus,
                  tone: 'bg-rose-50 text-rose-600',
                  onClick: () => {
                    setOpenMenuId(null);
                    onRemoveStudent(item.id);
                  },
                },
              ]}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-slate-300 bg-slate-50 p-5">
          <p className="text-sm font-semibold text-slate-700">Nenhum aluno encontrado para os filtros aplicados.</p>
          <p className="mt-1 text-sm text-slate-500">
            Ajuste os filtros ou vincule um membro do sistema master a esta turma.
          </p>
        </Card>
      )}

      <Card className="space-y-4 border-slate-200 bg-white p-4 shadow-[0_18px_40px_-24px_rgba(15,23,42,0.3)]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold text-slate-900">Visitantes</p>
            <p className="mt-1 text-xs font-medium text-slate-500">
              {visitors.length} {visitors.length === 1 ? 'visitante cadastrado' : 'visitantes cadastrados'}
            </p>
          </div>
        </div>
      </Card>

      {visitors.length > 0 ? (
        <div className="space-y-4">
          {visitors.map((visitor) => (
            <ListItemCard
              key={visitor.id}
              title={visitor.name}
              subtitle={visitor.whatsapp}
              onClick={() => onViewVisitorHistory(visitor.id)}
              leadingIcon={Users}
              leadingTone="from-violet-100 to-sky-100 text-violet-700"
              badges={[
                {
                  label: `${visitor.visitCount} ${visitor.visitCount === 1 ? 'visita' : 'visitas'}`,
                  className: 'bg-sky-50 text-sky-700',
                },
                {
                  label: visitor.lastVisitLabel,
                  className: 'bg-slate-100 text-slate-700',
                },
                ...(visitor.convertedToStudentId
                  ? [
                      {
                        label: 'Convertido em aluno',
                        className: 'bg-emerald-50 text-emerald-700',
                      },
                    ]
                  : []),
              ]}
              menuOpen={openMenuId === visitor.id}
              onToggleMenu={() => setOpenMenuId(openMenuId === visitor.id ? null : visitor.id)}
              actions={[
                {
                  label: 'Ver histórico',
                  icon: History,
                  tone: 'bg-emerald-50 text-emerald-600',
                  onClick: () => {
                    setOpenMenuId(null);
                    onViewVisitorHistory(visitor.id);
                  },
                },
                {
                  label: 'Converter em aluno',
                  icon: UserCheck,
                  tone: 'bg-sky-50 text-sky-700',
                  onClick: () => {
                    setOpenMenuId(null);
                    onConvertVisitor(visitor.id);
                  },
                },
              ]}
            />
          ))}
        </div>
      ) : (
        <Card className="border-dashed border-slate-300 bg-slate-50 p-5">
          <p className="text-sm font-semibold text-slate-700">Nenhum visitante cadastrado nesta turma.</p>
          <p className="mt-1 text-sm text-slate-500">
            Registre nome e WhatsApp para acompanhar novas visitas e futura conversão em aluno.
          </p>
        </Card>
      )}
    </section>
  );
};

export default ClassStudentsPanel;
